/**
 * Token Restoration Utilities
 * 
 * Restores tokens on page load if they were accidentally cleared
 * but refresh token still exists
 */

import logger from './logger';

/**
 * Attempts to restore tokens on page load if they're missing
 * but refresh token exists
 */
export const restoreTokensOnLoad = () => {
  try {
    if (typeof localStorage === 'undefined') {
      return false;
    }

    const isAuthenticated = localStorage.getItem('isAuthenticated');
    const authToken = localStorage.getItem('authToken');
    const refreshToken = localStorage.getItem('refreshToken');

    // If user is marked as authenticated but token is missing, but refresh token exists
    // This indicates tokens were accidentally cleared
    if (isAuthenticated === 'true' && !authToken && refreshToken) {
      logger.warn('Auth token missing but refresh token exists - tokens may have been accidentally cleared', {
        isAuthenticated,
        hasAuthToken: !!authToken,
        hasRefreshToken: !!refreshToken
      });
      
      // Don't auto-restore here - let the API client handle refresh on next 401
      // But log it for debugging
      return false;
    }

    return true;
  } catch (error) {
    logger.error('Error in restoreTokensOnLoad', { error });
    return false;
  }
};

/**
 * Verifies token integrity - ensures token and isAuthenticated flag are in sync
 */
export const verifyTokenIntegrity = () => {
  try {
    if (typeof localStorage === 'undefined') {
      return { valid: false, reason: 'localStorage unavailable' };
    }

    const isAuthenticated = localStorage.getItem('isAuthenticated');
    const authToken = localStorage.getItem('authToken');
    const refreshToken = localStorage.getItem('refreshToken');

    // If isAuthenticated is true but no tokens exist, something is wrong
    if (isAuthenticated === 'true' && !authToken && !refreshToken) {
      logger.error('CRITICAL: isAuthenticated=true but no tokens exist', {
        isAuthenticated,
        hasAuthToken: !!authToken,
        hasRefreshToken: !!refreshToken
      });
      return { 
        valid: false, 
        reason: 'isAuthenticated flag set but no tokens found',
        shouldClear: true 
      };
    }

    // If tokens exist but isAuthenticated is false, sync it
    if ((authToken || refreshToken) && isAuthenticated !== 'true') {
      logger.warn('Tokens exist but isAuthenticated flag is false - syncing', {
        hasAuthToken: !!authToken,
        hasRefreshToken: !!refreshToken,
        isAuthenticated
      });
      localStorage.setItem('isAuthenticated', 'true');
      return { valid: true, synced: true };
    }

    return { valid: true };
  } catch (error) {
    logger.error('Error verifying token integrity', { error });
    return { valid: false, reason: error.message };
  }
};





